import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getFingeringErrorDetail } from '../../../constants/fingeringErrorMessages';

interface ErrorModalProps {
  /** 失敗したジョブ ID (再投入時に元 entry を引くための識別子)。 */
  jobId: string;
  /** backend errorKind (mxl_missing / omr_timeout 等)。 未知 / 未指定なら汎用文言。 */
  errorKind?: string;
  /** 生の失敗詳細 (詳細欄に表示)。 */
  errorMessage?: string;
  /** [再投入] 押下。 設定を変えて投げ直す導線へ (配線は FE-2)。 */
  onRetry: () => void;
  /** [閉じる] 押下 / Escape / backdrop クリック。 */
  onClose: () => void;
}

/** errorKind に detail が無い / 未指定のときの汎用タイトル。 */
const FALLBACK_TITLE = '⚠️ 処理に失敗しました';

/**
 * issue #116 FE-1: 運指ジョブ失敗時のエラーモーダル。 成功時の CompletionModal の鏡像 (赤系)。
 * 文言は既存 getFingeringErrorDetail (title / cause / actions) を流用し新規文言は作らない。
 * CompletionModal の作法 (createPortal / Escape / backdrop+stopPropagation / role=dialog) を踏襲。
 */
const ErrorModal: React.FC<ErrorModalProps> = ({
  errorKind,
  errorMessage,
  onRetry,
  onClose,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  const detail = getFingeringErrorDetail(errorKind);
  const title = detail?.title ?? FALLBACK_TITLE;

  return createPortal(
    <div
      data-testid="error-modal-backdrop"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadein"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="error-modal-title"
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl p-6 w-[90vw] max-w-md animate-popin"
      >
        <div className="flex flex-col items-center text-center mb-5">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-3">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 id="error-modal-title" className="text-lg font-bold text-gray-800">
            {title}
          </h2>
        </div>

        {detail?.cause && (
          <p className="text-sm text-gray-600 mb-3">{detail.cause}</p>
        )}

        {detail && detail.actions.length > 0 && (
          <div className="mb-3">
            <p className="text-sm font-semibold text-gray-700 mb-1">おすすめの対処:</p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
              {detail.actions.map((action) => (
                <li key={action}>{action}</li>
              ))}
            </ul>
          </div>
        )}

        {errorMessage && (
          <p className="text-xs text-gray-400 break-all mb-4">詳細: {errorMessage}</p>
        )}

        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            onClick={onRetry}
            className="w-full px-4 py-2.5 rounded-lg text-sm font-bold text-white bg-red-600 hover:bg-red-700 shadow-sm transition-colors"
          >
            再投入
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ErrorModal;
